import React from "react";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

export default function Testimonials() {
  const { t } = useLanguage();

  return (
    <section
      data-testid="testimonials-section"
      className="relative overflow-hidden bg-[#FAFAFA] py-24 text-[#111] md:py-32 dark:bg-[#080808] dark:text-white"
    >
      {/* ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#F37021]/8 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <div className="mx-auto mb-14 max-w-2xl text-center md:mb-16">
          <span className="mb-4 inline-block text-xs font-semibold uppercase tracking-[0.3em] text-[#F37021]">
            — {t.testimonials.eyebrow}
          </span>
          <h2 className="font-display text-4xl font-bold leading-[1.08] sm:text-5xl md:text-6xl">
            {t.testimonials.title}
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
          {t.testimonials.items.map((item, i) => (
            <motion.div
              key={item.author}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="relative rounded-[28px] border border-black/6 bg-white p-7 transition-all hover:-translate-y-1 hover:border-[#F37021]/28 hover:shadow-[0_24px_60px_rgba(16,24,40,0.1)] dark:border-white/10 dark:bg-[linear-gradient(180deg,#101010_0%,#181818_100%)] dark:hover:bg-[#1D1D1D] dark:hover:shadow-[0_24px_60px_rgba(0,0,0,0.24)]"
              data-testid={`testimonial-card-${i}`}
            >
              <Quote className="mb-4 h-8 w-8 text-[#F37021]/40" strokeWidth={1.5} />

              <div className="mb-4 flex items-center gap-0.5">
                {["s1", "s2", "s3", "s4", "s5"].map((k) => (
                  <Star key={k} className="w-4 h-4 fill-[#F37021] text-[#F37021]" />
                ))}
              </div>

              <p className="mb-6 min-h-[108px] text-[15px] leading-relaxed text-[#4E5869] dark:text-[#D9E2EF]">
                "{item.quote}"
              </p>

              <div className="flex items-center gap-3 border-t border-black/6 pt-4 dark:border-white/10">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#F37021] to-[#E06015] text-sm font-semibold text-white">
                  {item.author.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-sm">{item.author}</div>
                  <div className="text-xs text-[#667084] dark:text-[#B3C0D1]">{item.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
